import { computed, debug, NetbankingAPI, observable } from "aaa";
import { AliasStore } from "components/alias/AliasStore";
import { action } from "mobx";
import { NetbankingAPIClient } from "network/APIClient";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import profileStore from "stores/ProfileStore";
import { amountToNumber, ZERO_AMOUNT } from "types/amount";
import { IGidDevice } from "types/ebvSecapp";
import { IAmount } from "types/fee";
import { ISignInfoResponse, ISigningInfo } from "types/signing";

export class GidSettingsStore {
    aliasStore = new AliasStore();
    @observable selectedLimitOption?: "noLimit" | "setLimit";
    @observable signInfo?: ISigningInfo;
    @observable newLimit: IAmount = ZERO_AMOUNT;
    @observable defaultLimit: IAmount = ZERO_AMOUNT;
    @observable currentLimit: IAmount = ZERO_AMOUNT;
    @observable isLimitMandatory: boolean = false;
    @observable gidAppLimitReadOnly: boolean = false;
    @observable userType: string = "";
    @observable status: string = "ACTIVE";
    @observable devices: IGidDevice[] = [];
    @observable deviceToDelete?: IGidDevice;
    @observable deleteDeviceResponse?: ISignInfoResponse;

    @observable isLoadingDevices = false;
    @observable isLoadingLimits = false;
    @observable isDeletingDevice = false;
    @observable _isLoading = false;

    @computed get isLoading() {
        return this._isLoading
            || this.isLoadingLimits
            || this.isLoadingDevices
            || this.aliasStore.isLoading
            || externalInterfaceStore.isLoadingSettings;
    }

    @computed get isJuniorReadOnly() {
        return this.userType === "JUNIOR" && this.gidAppLimitReadOnly === true;
    }

    @action async loadData() {
        try {
            this._isLoading = true;

            await Promise.all([
                this.loadSettings(),
                this.loadGidSettingsDevices(),
                this.loadLimits(),
            ]);

            this._isLoading = false;
        } catch (error) {
            debug.log("[GidSettingsStore] loadData error:", error);

            throw error;
        }
    }

    @action async loadSettings() {
        if (externalInterfaceStore.settings || externalInterfaceStore.isLoadingSettings) {
            // We've already loaded the settings -> get out
            return;
        }

        await externalInterfaceStore.loadSettings();
    }

    @action async loadLimits() {
        this.isLoadingLimits = true;

        try {
            // Load profile if not already loaded
            if (profileStore.profile) {
                const currentSettingsTarget = NetbankingAPI.getDailyLimit();
                const currentSettings = await NetbankingAPIClient.requestType(currentSettingsTarget);

                this.defaultLimit = currentSettings.gidAppDefaultLimit;
                this.currentLimit = currentSettings.gidAppDailyLimit;
                this.newLimit = currentSettings.gidAppDailyLimit;
                this.status = currentSettings.status;
                this.isLimitMandatory = currentSettings.gidAppLimitMandatory ?? false;
                this.gidAppLimitReadOnly = currentSettings.gidAppLimitReadOnly ?? false;
                this.userType = currentSettings.userType ?? "";

                // TESTDATA for NON GID users
                // this.newLimit = { value: 0, currency: "EUR", precision: 2 };
                // this.defaultLimit = { value: 300000, currency: "EUR", precision: 2 };
                // this.currentLimit = this.newLimit; // { value: 500000, currency: "EUR", precision: 2 };

                if (this.isLimitMandatory) {
                    this.selectedLimitOption = "setLimit";
                } else {
                    this.selectedLimitOption = amountToNumber(this.currentLimit) === 0 ? "noLimit" : "setLimit";
                }
            }

            this.isLoadingLimits = false;
        } catch (error) {
            this.isLoadingLimits = false;
            throw error;
        }
    }

    @action async loadGidSettingsDevices() {
        const target = NetbankingAPI.getGidSettingsDevices();
        this.isLoadingDevices = true;

        try {
            this.devices = (await NetbankingAPIClient.requestType(target))?.devices.filter(device =>
                device.status === "ACTIVE" && device.type === "GEORGE_TOKEN"
            );
            debug.log("### gidDevices", this.devices);
            this.isLoadingDevices = false;
        } catch (error) {
            // if no devices are found the endpoint returns 404
            //if (error.statusCode && error.statusCode === 404) {
            if ((error as any).statusCode && (error as any).statusCode === 404) {
                this.devices = [];
            } else {
                throw error;
            }
        } finally {
            this.isLoadingDevices = false;
        }
    }

    @action async deleteGidSettingsDevice() {
        if (!this.deviceToDelete) {
            return;
        }

        const deviceId = this.deviceToDelete.id.toString();
        const target = NetbankingAPI.deleteGidSettingsDevice(deviceId);

        try {
            this.isDeletingDevice = true;
            // this.deleteDeviceResponse = await NetbankingAPIClient.requestType(target);
            await NetbankingAPIClient.request(target);
            this.isDeletingDevice = false;
        } catch (error) {
            this.isDeletingDevice = false;
            throw error;
        }

        // Reload devices after delete
        await this.loadGidSettingsDevices();
    }

    @action async postLimitChange() {
        if (profileStore.profile && this.newLimit) {
            try {
                this._isLoading = true;

                const limitAmount = (this.isLimitMandatory || this.selectedLimitOption === "setLimit")
                    ? this.newLimit
                    : ZERO_AMOUNT;

                const target = NetbankingAPI.putDailyLimit(limitAmount, this.status);

                const response = await NetbankingAPIClient.requestType(target);

                this.signInfo = response.signInfo;

                this._isLoading = false;
            } catch (error) {
                this._isLoading = false;
                throw error;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/loginManagement/gidSettings/GidSettingsStore.ts